// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { GANTT_MARKER_LABEL_BAND_PX } from '@/billing/lifecycle/gantt-math';
import { Box, Typography } from '@mui/material';

interface GanttVerticalMarkerProps {
  leftPct: number;
  color: string;
  variant: 'solid' | 'dashed';
  labelText: string;
  labelPosition: 'top' | 'bottom';
}

// Vertical marker line inside the per-invoice Gantt axis container, with
// a label that rides in the reserved marker-label band either above
// (`top`) or below (`bottom`) the chart rows. The marker line itself is
// inset from the container's top/bottom by the label-band height so it
// only covers the chart area; labels sit inside the band so they don't
// depend on overflow being allowed on the axis container.
export function GanttVerticalMarker(props: GanttVerticalMarkerProps) {
  const { leftPct, color, variant, labelText, labelPosition } = props;
  // Label sits 2px inside the band from its marker-box edge. With the
  // marker-box inset equal to the band, that puts the label 2px inside
  // the container's top (or bottom) edge.
  const labelSx =
    labelPosition === 'top'
      ? { top: -GANTT_MARKER_LABEL_BAND_PX + 2 }
      : { bottom: -GANTT_MARKER_LABEL_BAND_PX + 2 };
  // Anchor the label so its visible box stays inside the chart container:
  // translate by `-leftPct%` of the label's own width. At leftPct=0 the
  // label starts at the marker (translateX(0) — no shift). At leftPct=100
  // it ends at the marker (translateX(-100%) — fully shifted left). At
  // the midpoint it remains centered. This keeps the label visually
  // bound to the marker while preventing horizontal overflow even when
  // the marker sits at the extreme left or right edge of the axis.
  const translateXPct = -leftPct;
  return (
    <Box
      sx={{
        position: 'absolute',
        top: `${GANTT_MARKER_LABEL_BAND_PX}px`,
        bottom: `${GANTT_MARKER_LABEL_BAND_PX}px`,
        left: `${leftPct}%`,
        width: 0,
        borderLeft: 1,
        borderLeftStyle: variant,
        borderColor: color,
        pointerEvents: 'none',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          ...labelSx,
          transform: `translateX(${translateXPct.toFixed(2)}%)`,
          whiteSpace: 'nowrap',
          color,
          fontSize: 10,
          fontWeight: 600,
          backgroundColor: 'background.paper',
          padding: '0 4px',
        }}
      >
        {labelText}
      </Typography>
    </Box>
  );
}
